(function attachJudesProbierPricing(root, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.JudesProbierPricing = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, function createJudesProbierPricing() {
  'use strict';

  const identityVersion = 'probier-price-identity-1';

  const CREATE = `mutation ProbierQuote($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id totalQuantity discountCodes { code applicable }
        cost { subtotalAmount { amount currencyCode } }
        lines(first: 250) {
          pageInfo { hasNextPage }
          nodes {
            id quantity attributes { key value }
            merchandise { ... on ProductVariant { id } }
            sellingPlanAllocation { sellingPlan { id } }
            cost { amountPerQuantity { amount currencyCode } }
          }
        }
      }
      userErrors { code message }
      warnings { code message }
    }
  }`;
  const REMOVE = `mutation ProbierQuoteCleanup($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { totalQuantity }
      userErrors { code message }
      warnings { code message }
    }
  }`;

  function assert(condition, message) {
    if (!condition) throw new Error(message);
  }

  function numericId(value, type) {
    if (typeof value === 'number') assert(Number.isSafeInteger(value), 'Invalid merchandise identity');
    const raw = String(value == null ? '' : value);
    const prefix = `gid://shopify/${type}/`;
    const id = raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
    assert(/^[1-9]\d*$/.test(id), 'Invalid merchandise identity');
    return id;
  }

  function normalizeProperties(properties) {
    assert(!properties || (typeof properties === 'object' && !Array.isArray(properties)), 'Invalid line properties');
    return Object.fromEntries(Object.entries(properties || {}).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0).map(([key, value]) => {
      assert(key && ['string', 'number', 'boolean'].includes(typeof value), 'Invalid line properties');
      assert(typeof value !== 'number' || Number.isFinite(value), 'Invalid line properties');
      return [key, String(value)];
    }));
  }

  function normalizeItems(items) {
    assert(Array.isArray(items) && items.length > 0 && items.length <= 250, 'Invalid quote basket');
    const aggregate = new Map();
    for (const item of items) {
      assert(item && typeof item === 'object', 'Invalid quote line');
      const quantity = Number(item.quantity);
      // Quantity is GraphQL Int; 250 bounds the lines array, not units in the cart.
      assert(Number.isSafeInteger(quantity) && quantity > 0 && quantity <= 2147483647, 'Invalid quote quantity');
      const normalized = {
        id: numericId(item.variant_id || item.id, 'ProductVariant'),
        properties: normalizeProperties(item.properties),
        selling_plan: null
      };
      const plan = item.selling_plan ?? item.sellingPlanId ?? item.selling_plan_allocation?.selling_plan?.id;
      if (plan != null && plan !== '') normalized.selling_plan = numericId(plan, 'SellingPlan');
      const key = JSON.stringify(normalized);
      const previous = aggregate.get(key);
      if (previous) {
        previous.quantity += quantity;
        assert(previous.quantity <= 2147483647, 'Invalid quote quantity');
      }
      else aggregate.set(key, { ...normalized, quantity });
    }
    return [...aggregate.entries()].sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0).map(([, item]) => item);
  }

  function context(options) {
    const country = String(options.country || '').toUpperCase();
    const currency = String(options.currency || '').toUpperCase();
    const discountCode = String(options.discountCode || '').trim().toLowerCase();
    assert(/^[A-Z]{2}$/.test(country) && /^[A-Z]{3}$/.test(currency), 'Invalid quote market');
    assert(discountCode.length > 0, 'Missing quote discount');
    return { country, currency, discountCode, items: normalizeItems(options.items) };
  }

  // Canonical identity, not a security signature. TTL and current-cart binding belong to the caller.
  function fingerprint(options) {
    return JSON.stringify(context(options));
  }

  function moneyMinor(money, currency) {
    assert(money && money.currencyCode === currency && typeof money.amount === 'string', 'Invalid quote currency or amount');
    // Shopify Ajax uses currency units times 100 even for JPY. Do not use Intl's
    // currency fraction count to convert Storefront decimal amounts to Ajax money.
    const match = /^(0|[1-9]\d*)(?:\.(\d{1,2})0*)?$/.exec(money.amount);
    assert(match, 'Invalid native money amount');
    const minor = Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0'));
    assert(Number.isSafeInteger(minor) && minor >= 0, 'Invalid native money amount');
    return minor;
  }

  function noProblems(body, payload, cleanup = false) {
    assert(body && (!body.errors || (Array.isArray(body.errors) && body.errors.length === 0)), 'Native quote request failed');
    assert(payload && Array.isArray(payload.userErrors) && payload.userErrors.length === 0, 'Native quote was rejected');
    assert(Array.isArray(payload.warnings) && payload.warnings.every(w => cleanup && w.code === 'DISCOUNT_NO_ENTITLED_LINE_ITEMS'), 'Native quote returned a warning');
  }

  async function quote(options) {
    const input = context(options);
    assert(typeof options.fetchFn === 'function', 'Missing quote transport');
    const endpoint = options.endpoint || '/api/2026-01/graphql.json';
    assert(/^\/api\/\d{4}-\d{2}\/graphql\.json$/.test(endpoint), 'Invalid quote endpoint');
    async function request(query, variables) {
      const response = await options.fetchFn(endpoint, {
        method: 'POST', credentials: 'omit', cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, variables })
      });
      // Never expose native cart IDs or opaque server messages to callers.
      assert(response.ok, 'Native quote request failed');
      return response.json();
    }

    let cart;
    let failure;
    let result;
    try {
      const body = await request(CREATE, { input: {
        buyerIdentity: { countryCode: input.country },
        discountCodes: [input.discountCode],
        lines: input.items.map(item => ({
          merchandiseId: `gid://shopify/ProductVariant/${item.id}`,
          quantity: item.quantity,
          attributes: Object.entries(item.properties).map(([key, value]) => ({ key, value })),
          ...(item.selling_plan ? { sellingPlanId: `gid://shopify/SellingPlan/${item.selling_plan}` } : {})
        }))
      } });
      const payload = body?.data?.cartCreate;
      cart = payload?.cart;
      noProblems(body, payload);
      assert(cart && typeof cart.id === 'string' && cart.id && Array.isArray(cart.lines?.nodes), 'Native quote basket is missing');
      assert(cart.lines.pageInfo?.hasNextPage === false, 'Native quote basket is incomplete');
      assert(Array.isArray(cart.discountCodes) && cart.discountCodes.length === 1
        && String(cart.discountCodes[0].code).toLowerCase() === input.discountCode
        && cart.discountCodes[0].applicable === true, 'Native quote discount is not applicable');
      const actualItems = normalizeItems(cart.lines.nodes.map(line => {
        assert(Array.isArray(line.attributes) && new Set(line.attributes.map(a => a.key)).size === line.attributes.length, 'Invalid native line properties');
        return {
          id: line.merchandise?.id, quantity: line.quantity,
          properties: Object.fromEntries(line.attributes.map(a => [a.key, a.value])),
          selling_plan: line.sellingPlanAllocation?.sellingPlan?.id
        };
      }));
      assert(JSON.stringify(actualItems) === JSON.stringify(input.items)
        && cart.totalQuantity === input.items.reduce((sum, item) => sum + item.quantity, 0), 'Native quote basket does not match selection');
      const retailValueMinor = cart.lines.nodes.reduce((sum, line) => sum + moneyMinor(line.cost?.amountPerQuantity, input.currency) * line.quantity, 0);
      const finalPriceMinor = moneyMinor(cart.cost?.subtotalAmount, input.currency);
      assert(Number.isSafeInteger(retailValueMinor) && finalPriceMinor <= retailValueMinor, 'Invalid native quote totals');
      const catalogPrices = {};
      for (const line of cart.lines.nodes) {
        const id = numericId(line.merchandise?.id, 'ProductVariant');
        const unitPrice = moneyMinor(line.cost?.amountPerQuantity, input.currency);
        assert(catalogPrices[id] === undefined || catalogPrices[id] === unitPrice, 'Native catalog price differs within basket');
        catalogPrices[id] = unitPrice;
      }
      result = {
        currency: input.currency, retailValueMinor, finalPriceMinor,
        discountAmountMinor: retailValueMinor - finalPriceMinor,
        fingerprint: JSON.stringify(input), items: input.items, catalogPrices
      };
      // Display only: never share the authoritative result with an observer.
      // The returned promise still waits for mandatory cleanup below.
      if (typeof options.onVerifiedPrice === 'function') {
        options.onVerifiedPrice(JSON.parse(JSON.stringify(result)));
      }
    } catch (error) {
      failure = new Error('The native set price could not be confirmed');
    } finally {
      if (cart) {
        try {
          assert(typeof cart.id === 'string' && cart.id && Array.isArray(cart.lines?.nodes), 'Missing quote cleanup identity');
          const lineIds = cart.lines.nodes.map(line => line.id);
          assert(lineIds.every(id => typeof id === 'string' && id.length > 0), 'Missing quote cleanup lines');
          if (lineIds.length) {
            const body = await request(REMOVE, { cartId: cart.id, lineIds });
            const payload = body?.data?.cartLinesRemove;
            noProblems(body, payload, true);
            assert(payload.cart?.totalQuantity === 0, 'Quote cleanup is incomplete');
          } else assert(cart.totalQuantity === 0, 'Quote cleanup is incomplete');
        } catch (error) {
          failure = new Error('The temporary price quote could not be cleared');
          failure.quoteCleanupFailed = true;
        }
      }
    }
    if (failure) throw failure;
    return result;
  }

  return { quote, normalizeItems, fingerprint, moneyMinor, identityVersion };
}));
